import {
	IUseSessionDto,
	useSession,
} from "@AppBuilderLib/entities/session/model/useSession";
import {useAppBuilderCustomParameters} from "@AppBuilderLib/features/appbuilder/model/useAppBuilderCustomParameters";
import {useAppBuilderResetValueOverrides} from "@AppBuilderLib/features/appbuilder/model/useAppBuilderResetValueOverrides";
import {IAppBuilder, IAppBuilderSettingsSession} from "../config/appbuilder";
import {parseAppBuilderSkeleton} from "../config/parseAppBuilderJson";
// Eidos (5 ott 2026): sul telefono le tendine diventano schede orizzontali (vedi src/eidos/tabsTelefono.ts)
import {adattaPerTelefono, ascoltaSchermoTelefono} from "../../../../eidos/tabsTelefono";

import {useShapeDiverStoreSession} from "@AppBuilderLib/entities/session/model/useShapeDiverStoreSession";
import {Logger} from "@AppBuilderLib/shared/lib/logger";
import {useShapeDiverStoreProcessManager} from "@AppBuilderLib/shared/model/useShapeDiverStoreProcessManager";
import {IOutputApi, ITreeNode, OutputApiData} from "@shapediver/viewer.session";
import {useCallback, useEffect, useRef, useState} from "react";
import {useAppBuilderInstances} from "./useAppBuilderInstances";

/**
 * Hook for creating a session with a ShapeDiver model using the ShapeDiver 3D Viewer.
 *
 * Registers all parameters and exports defined by the model as abstracted
 * parameters and exports for use by the UI components.
 *
 * TODO SS-7484 This hook also registers custom parameters and UI elements defined by a data output component
 * of the model named "AppBuilder". Updates of the custom parameter values are fed back to the model as JSON into
 * a text input named "AppBuilder".
 *
 * @param props session to start
 * @param appBuilderOverride optional AppBuilder data to override the data from the model
 * @returns
 */
export function useSessionWithAppBuilder(
	props: (IUseSessionDto & IAppBuilderSettingsSession) | undefined,
	appBuilderOverride?: IAppBuilder,
) {
	const namespace = props?.id ?? "";

	// start session and register parameters and exports
	const {sessionApi, error: sessionError} = useSession(props);
	const sessionInitialized = !!sessionApi;

	const {addOutputUpdateCallback} = useShapeDiverStoreSession();
	const {
		addProcess,
		createProcessManager,
		removeProcessManager,
		processManagers,
	} = useShapeDiverStoreProcessManager();
	const [parsedData, setParsedData] = useState<
		IAppBuilder | Error | undefined
	>(undefined);
	const [outputApi, setOutputApi] = useState<IOutputApi | undefined>(
		undefined,
	);
	const [appBuilderOutputId, setAppBuilderOutputId] = useState<
		string | undefined
	>(undefined);
	const [sessionSettings, setSessionSettings] = useState<
		(IUseSessionDto & IAppBuilderSettingsSession) | undefined
	>(props);

	const initialProcessManagerIdRef = useRef<string | undefined>(undefined);
	// create an initial process manager to handle the session creation process
	// and bridge the gap until the callback for the app builder output is called
	useEffect(() => {
		if (!initialProcessManagerIdRef.current) {
			initialProcessManagerIdRef.current =
				createProcessManager(namespace);
		}
	}, [namespace]);

	const processManagerIdRef = useRef<string | undefined>(undefined);
	const [appBuilderProcessManagerId, setAppBuilderProcessManagerId] =
		useState<string | undefined>(undefined);
	const resolveProcessManagerBridgeRef = useRef<(() => void) | undefined>(
		undefined,
	);

	/**
	 * Parse AppBuilder layout from the model output, or use settings override as-is.
	 * Override is not re-validated here (validated in settings JSON when env is on).
	 */
	const validationResult = useCallback(
		(data: IAppBuilder | string | undefined) => {
			if (appBuilderOverride && sessionInitialized) {
				if (data)
					Logger.debug("Overriding AppBuilder data from settings!");

				return appBuilderOverride;
			}
			if (!data) return undefined;
			if (typeof data === "string") {
				let parsedJson: unknown;
				try {
					parsedJson = JSON.parse(data);
				} catch (e: any) {
					return new Error(
						`Parsing AppBuilder JSON data failed: ${e?.message ?? "unknown error"}`,
					);
				}

				return adattaPerTelefono(parseAppBuilderSkeleton(parsedJson));
			}

			return adattaPerTelefono(parseAppBuilderSkeleton(data));
		},
		[appBuilderOverride, sessionInitialized],
	);

	// Eidos (9 ott 2026): nel negozio il configuratore parte in una finestra nascosta (larga 0 = "telefono", quindi
	// schede) e si apre dopo, larga come il computer: quando la larghezza passa la soglia dei 600 px l'ultimo JSON
	// arrivato dal modello si ridisegna (tendine sul computer, schede sul telefono). Nessun calcolo ShapeDiver.
	const ultimoDatoAppBuilderRef = useRef<IAppBuilder | string | undefined>(
		undefined,
	);
	useEffect(
		() =>
			ascoltaSchermoTelefono(() => {
				const dato = ultimoDatoAppBuilderRef.current;
				if (dato !== undefined) setParsedData(validationResult(dato));
			}),
		[validationResult],
	);

	useEffect(() => {
		if (!sessionApi) {
			setAppBuilderOutputId(undefined);
			return;
		}

		if (props?.loadPlatformSettingsFromViewer !== undefined) {
			if (props.loadPlatformSettingsFromViewer === "platform") {
				// Load platform settings from viewer
				setSessionSettings({
					...(props as IAppBuilderSettingsSession),
					acceptRejectMode: sessionApi.parametersCommit,
					hideAttributeVisualization:
						sessionApi.hideAttributeVisualization,
					hideJsonMenu: sessionApi.hideJsonMenu,
					hideSavedStates: sessionApi.hideSavedStates,
					hideDesktopClients: sessionApi.hideDesktopClients,
					hideExports: sessionApi.hideExports,
				});
			} else if (props.loadPlatformSettingsFromViewer === "iframe") {
				// Load iframe settings from viewer
				setSessionSettings({
					...(props as IAppBuilderSettingsSession),
					acceptRejectMode: sessionApi.parametersCommit,
					hideAttributeVisualization:
						sessionApi.hideAttributeVisualizationIframe,
					hideJsonMenu: sessionApi.hideJsonMenuIframe,
					hideSavedStates: sessionApi.hideSavedStatesIframe,
					hideExports: sessionApi.hideExportsIframe,
				});
			}
		} else {
			setSessionSettings(props);
		}

		const outputs = sessionApi.getOutputByName(CUSTOM_DATA_OUTPUT_NAME);
		const outputId = outputs.length > 0 ? outputs[0].id : "";

		if (outputId === "") {
			// as there is not app builder output, we can set the parsed data to undefined
			// unless the app builder override is set, in which case we set it to the override
			setParsedData(validationResult(undefined));
		}

		setAppBuilderOutputId(outputId);
	}, [sessionApi, props]);

	/**
	 * Callback for updating the AppBuilder data.
	 * This callback already validates the data and creates a process manager if necessary.
	 * This has to be done synchronously to ensure that the process manager is created before the scene update is triggered.
	 *
	 * @param newNode
	 */
	const cb = useCallback(
		(newNode?: ITreeNode) => {
			const api = (
				newNode?.data.find((d) => d instanceof OutputApiData) as
					| OutputApiData
					| undefined
			)?.api;
			setOutputApi(api);

			const outputData = api?.content?.[0]?.data as
				| IAppBuilder
				| string
				| undefined;
			ultimoDatoAppBuilderRef.current = outputData;
			const parsedData = validationResult(outputData);
			const appBuilderData =
				parsedData instanceof Error ? undefined : parsedData;

			// For instances, this check and the creation of the process manager
			// has to be done here, as otherwise the viewer would already
			// update the scene before the instances are processed
			const instancedSessions = !!appBuilderData?.instances;

			// if there are instances defined in the app builder data, we need a process manager
			let newProcessManagerId: string | undefined = undefined;
			let resolveBridge: (() => void) | undefined = undefined;
			if (instancedSessions && sessionApi) {
				newProcessManagerId = createProcessManager(sessionApi.id);
				const bridgePromise = new Promise<void>((resolve) => {
					resolveBridge = resolve;
				});
				addProcess(newProcessManagerId, {
					name: "AppBuilder Instance Bridge",
					promise: bridgePromise,
				});
			}

			// if there is still a processManager active, we remove it
			// and assign the id of the new one
			// we have to do this after creating the new one, as otherwise
			// the viewer would already update the scene before the instances are processed
			if (processManagerIdRef.current) {
				removeProcessManager(processManagerIdRef.current);
			}

			// assign the new process manager id (or undefined) to the ref
			processManagerIdRef.current = newProcessManagerId;
			resolveProcessManagerBridgeRef.current = resolveBridge;
			setAppBuilderProcessManagerId(newProcessManagerId);

			setParsedData(parsedData);
		},
		[
			addProcess,
			createProcessManager,
			namespace,
			removeProcessManager,
			sessionApi,
			validationResult,
		],
	);

	// if the process manager id is not valid anymore, reset it
	useEffect(() => {
		if (!processManagerIdRef.current) return;

		if (!processManagers[processManagerIdRef.current]) {
			const removedProcessManagerId = processManagerIdRef.current;
			processManagerIdRef.current = undefined;
			resolveProcessManagerBridgeRef.current = undefined;
			setAppBuilderProcessManagerId((current) =>
				current === removedProcessManagerId ? undefined : current,
			);
		}
	}, [processManagers]);

	useEffect(() => {
		if (appBuilderOutputId === undefined) return;
		const removeOutputUpdateCallback = addOutputUpdateCallback(
			namespace,
			appBuilderOutputId,
			cb,
		);

		// whether we have an app builder output or not is not relevant here
		// the initial process creation is done as soon as the session data has been parsed
		if (initialProcessManagerIdRef.current) {
			removeProcessManager(initialProcessManagerIdRef.current);
			initialProcessManagerIdRef.current = undefined;
		}

		return removeOutputUpdateCallback;
	}, [namespace, appBuilderOutputId, cb]);

	useEffect(() => {
		Logger.debug(CUSTOM_DATA_OUTPUT_NAME, parsedData);
	}, [parsedData]);

	const error =
		sessionError ?? (parsedData instanceof Error ? parsedData : undefined);
	const appBuilderData = parsedData instanceof Error ? undefined : parsedData;
	const hasAppBuilderOutput = !!outputApi;
	// "" = session inspected, no AppBuilder output (parsedData stays undefined)
	const appBuilderParseSettled =
		parsedData !== undefined || appBuilderOutputId === "";

	// register custom parameters
	const {loaded: customParametersLoaded} = useAppBuilderCustomParameters({
		sessionApi,
		appBuilderData,
		acceptRejectMode: props?.acceptRejectMode,
	});

	// register the reset values defined by the overrides of parameter references
	useAppBuilderResetValueOverrides({namespace, appBuilderData});

	const handleProcessManagerAttached = useCallback(
		(processManagerId: string | undefined) => {
			if (!processManagerId) return;
			if (processManagerIdRef.current !== processManagerId) return;

			resolveProcessManagerBridgeRef.current?.();
			resolveProcessManagerBridgeRef.current = undefined;
			processManagerIdRef.current = undefined;
			setAppBuilderProcessManagerId((current) =>
				current === processManagerId ? undefined : current,
			);
		},
		[],
	);

	// resolve any orphaned bridge on unmount
	useEffect(() => {
		return () => {
			resolveProcessManagerBridgeRef.current?.();
			resolveProcessManagerBridgeRef.current = undefined;
			if (processManagerIdRef.current) {
				removeProcessManager(processManagerIdRef.current);
			}
			if (initialProcessManagerIdRef.current)
				removeProcessManager(initialProcessManagerIdRef.current);
		};
	}, [removeProcessManager]);

	// create the instances defined in the AppBuilder data
	useAppBuilderInstances({
		namespace,
		sessionApi,
		appBuilderData,
		processManagerId: appBuilderProcessManagerId,
		onProcessManagerAttached: handleProcessManagerAttached,
	});

	return {
		sessionApi,
		namespace,
		error,
		appBuilderData,
		hasAppBuilderOutput,
		appBuilderParseSettled,
		customParametersLoaded,
		sessionSettings,
	};
}

/**
 * Name of data output used to define the AppBuilder UI
 */
const CUSTOM_DATA_OUTPUT_NAME = "AppBuilder";
